/**
 * Dropdown - Shared select dropdown component
 *
 * A styled HTML select element for simple dropdowns.
 * For dropdowns requiring custom content (multi-select, rich options), use react-select instead.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

export interface DropdownOption<T extends string = string> {
  value: T;
  label: string;
}

export interface DropdownProps<T extends string = string> {
  /** Currently selected value */
  value: T | null;
  /** Options to display */
  options: DropdownOption<T>[];
  /** Change handler */
  onChange: (value: T | null) => void;
  /** Placeholder for null/empty value */
  placeholder?: string;
  /** Include an empty option for null selection */
  allowEmpty?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Additional CSS classes for the select element */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** ID for label association */
  id?: string;
  /** Aria label for accessibility */
  'aria-label'?: string;
}

/**
 * Base classes applied to all dropdowns
 */
const baseClasses =
  'rounded-md border bg-zinc-800/80 text-zinc-300 focus:border-aether focus:outline-none transition-colors cursor-pointer';

/**
 * Size-specific classes
 */
const sizeClasses = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-2 py-1 text-sm',
};

export default function Dropdown<T extends string = string>({
  value,
  options,
  onChange,
  placeholder = 'Select...',
  allowEmpty = false,
  size = 'md',
  className,
  disabled = false,
  id,
  'aria-label': ariaLabel,
}: DropdownProps<T>): ReactElement {
  return (
    <select
      id={id}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? (e.target.value as T) : null)}
      disabled={disabled}
      aria-label={ariaLabel}
      className={clsx(baseClasses, sizeClasses[size], 'border-zinc-700', disabled && 'cursor-not-allowed opacity-50', className)}
    >
      {(allowEmpty || value === null) && (
        <option value="" className="bg-zinc-800 text-zinc-100">
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} className="bg-zinc-800 text-zinc-100">
          {opt.label}
        </option>
      ))}
    </select>
  );
}
